import { QueryClient } from "@tanstack/react-query";

/**
 * The single React Query client for the app, shared by three callers:
 * - the app entry wraps the tree in a `QueryClientProvider` with it;
 * - it is handed to the router as context, so route loaders can
 *   `ensureQueryData` into the very cache the components then read;
 * - `document-store.ts` reaches it via {@link getBrowserQueryClient} to read
 *   and write the document cache imperatively from non-React code.
 *
 * All three must hit the *same* instance for an imperative `setQueryData` to
 * re-render components subscribed via `useQuery`. In an SPA that is simply a
 * module singleton — the per-request branch the Next.js version needed (a
 * fresh client per server render, so requests never shared a cache) has no
 * meaning here and is gone.
 */

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Stale-while-revalidate: a page you have seen renders from the cache
        // at once, and anything older than this is refetched behind it — so
        // the screen is never more than one request behind the server. It is
        // not 0 only so that a route loader's `ensureQueryData` (or a hover
        // preload moments earlier) is reused by the component that mounts
        // right after, instead of being fetched twice. Reference data that
        // rarely changes (plan, role catalog, letterheads) sets its own,
        // longer staleTime.
        staleTime: 5 * 1000,
        // How long a page's data outlives the page, so that going back to it
        // shows the last answer instead of a skeleton. Memory only — nothing
        // is persisted, and sign-out or a company switch drops it all.
        gcTime: 30 * 60 * 1000,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

/** The app's query client. */
export function getQueryClient(): QueryClient {
  browserQueryClient ??= makeQueryClient();
  return browserQueryClient;
}

/**
 * The singleton, for imperative cache access *outside* React. Kept as a
 * separate named export because callers treat `null` as "no client yet"; in
 * the SPA it always resolves, but the call sites' guards stay valid.
 */
export function getBrowserQueryClient(): QueryClient | null {
  return getQueryClient();
}
